import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';

const SearchableMedicineDropdownByStock = ({
    value = '',
    onChange,
    placeholder = 'Select Medicine...',
    disabled = false,
    className = '',
    error = false,
    autoFocus = false,
    defaultDisplay = '',
    onKeyDown: parentOnKeyDown // <-- Optional parent handler
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [medicines, setMedicines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const mainDivRef = useRef(null);
    const itemRefs = useRef([]);

    // Auto-focus logic
    useEffect(() => {
        if (autoFocus && !disabled) {
            setIsOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [autoFocus, disabled]);

    // Reset highlighted index when medicines change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [medicines]);

    // Scroll to highlighted item
    useEffect(() => {
        if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
            itemRefs.current[highlightedIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [highlightedIndex]);

    // Find selected medicine
    useEffect(() => {
        if (value && medicines.length > 0) {
            const found = medicines.find(med => med.id === parseInt(value));
            setSelectedMedicine(found || null);
        } else {
            setSelectedMedicine(null);
        }
    }, [value, medicines]);

    // Debounced search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (isOpen) fetchMedicines(searchTerm);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm, isOpen]);

    useEffect(() => {
        if (isOpen && medicines.length === 0) fetchMedicines('');
    }, [isOpen]);

    useEffect(() => {
        if (value && medicines.length === 0 && !loading) fetchMedicines('');
    }, [value, medicines.length, loading]);

    // Click outside close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchMedicines = async (search = '') => {
        setLoading(true);
        try {
            let url = '/medicine/by-stock?limit=100&page=1';
            if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
            const response = await axiosInstance.get(url);
            const medicineData = response.data.medicines || response.data.data || [];
            setMedicines(medicineData);
        } catch (error) {
            console.error('Error fetching medicines:', error);
            setMedicines([]);
        } finally {
            setLoading(false);
        }
    };

    const openDropdown = () => {
        if (disabled) return;
        setIsOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    const handleMedicineSelect = (medicine) => {
        setSelectedMedicine(medicine);
        onChange?.(medicine);
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedMedicine(null);
        setSearchTerm('');
        onChange?.({ id: '', name: '' });
    };

    // MAIN FIX: Enter press = Open dropdown + focus search
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            openDropdown();
        }

        // Agar parent ne apna onKeyDown diya ho (jaise NewInvoice wala)
        if (parentOnKeyDown) {
            parentOnKeyDown(e);
        }
    };

    const handleInputKeyDown = (e) => {
        if (medicines.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex(prev => (prev < medicines.length - 1 ? prev + 1 : prev));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
                return;
            }
            if (e.key === 'Enter' && highlightedIndex >= 0) {
                e.preventDefault();
                e.stopPropagation();
                if (medicines[highlightedIndex]) {
                    handleMedicineSelect(medicines[highlightedIndex]);
                }
                return;
            }
        }
        if (parentOnKeyDown) parentOnKeyDown(e);
    };

    const displayText = selectedMedicine
        ? `${selectedMedicine.name} - ${selectedMedicine.manufacturer}`
        : defaultDisplay || placeholder;

    const borderColor = error
        ? 'border-red-300 focus-within:border-red-500'
        : 'border-gray-300 focus-within:border-teal-500';

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Main Clickable & Focusable Div */}
            <div
                ref={mainDivRef}
                tabIndex={disabled ? -1 : 0}  // ← Yeh zaroori hai Enter ke liye
                className={`
                    w-full px-3 py-2 bg-white border rounded-lg cursor-pointer outline-none
                    flex items-center justify-between ring-2 ring-transparent
                    ${borderColor} ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'}
                `}
                onClick={openDropdown}
                onKeyDown={handleKeyDown}   // ← Yeh sabse important fix
                onFocus={() => !isOpen && !disabled && mainDivRef.current?.classList.add('ring-teal-500')}
                onBlur={() => mainDivRef.current?.classList.remove('ring-teal-500')}
            >
                <span className={`truncate flex-1 ${selectedMedicine ? 'text-gray-900' : 'text-gray-500'} ${disabled ? 'text-gray-400' : ''}`}>
                    {displayText}
                </span>

                <div className="flex items-center gap-2">
                    {selectedMedicine && !disabled && (
                        <button type="button" onClick={handleClear} className="p-1 hover:bg-gray-100 rounded z-10">
                            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                        </button>
                    )}
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder="Search medicines..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto max-h-60">
                        {loading ? (
                            <div className="p-4 text-center text-gray-500">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="mt-2">Loading...</p>
                            </div>
                        ) : medicines.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                {searchTerm ? 'No medicines found' : 'No medicines available'}
                            </div>
                        ) : (
                            medicines.map((medicine, index) => (
                                <div
                                    key={medicine.id}
                                    ref={el => itemRefs.current[index] = el}
                                    onClick={() => handleMedicineSelect(medicine)}
                                    className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-teal-50 
                                        ${selectedMedicine?.id === medicine.id ? 'bg-teal-100' : ''}
                                        ${highlightedIndex === index ? 'bg-teal-50 ring-1 ring-inset ring-teal-200' : ''}
                                    `}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 truncate">{medicine.name}</div>
                                            <div className="text-sm text-gray-500 truncate">
                                                {medicine.manufacturer} • {medicine.uom} • {medicine.pack}
                                            </div>
                                            {medicine.generic_name && medicine.generic_name !== 'NA' && (
                                                <div className="text-xs text-gray-400 truncate">{medicine.generic_name}</div>
                                            )}
                                        </div>
                                        {selectedMedicine?.id === medicine.id && <Check className="w-4 h-4 text-teal-600 ml-2" />}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {medicines.length > 0 && (
                        <div className="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 text-center">
                            Showing {medicines.length} medicine{medicines.length !== 1 ? 's' : ''}
                            {searchTerm && ` for "${searchTerm}"`}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableMedicineDropdownByStock;